import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AG_GRID_AUTO_COL_ID } from 'common/constants';
import { useFlagValue } from 'hooks';
import { useSelectAllInvoices } from 'pages/HighVolumeLandingPage/components/useSelectAllInvoices';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { HEADER_CHECKBOX_CELL_SELECTOR } from './constants';
import { getInvoiceIdsToSelect } from './helpers';
import {
  clearSelectedInvoices,
  getPaymentMethods,
  getRowData,
  getRowDataByVendor,
  getSelectedInvoicesLength,
  selectInvoices
} from './slice';
import { StyledCheckbox } from './styled';
import { useCustomSort } from './useCustomSort';
import type { IHeaderParams } from 'ag-grid-community';

export const HeaderCheckboxRenderer = ({
  api
}: IHeaderParams): React.JSX.Element => {
  const rowData = useAppSelector(getRowData);
  const dispatch = useAppDispatch();
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const paymentMethods = useAppSelector(getPaymentMethods);
  const selectedInvoicesLength = useAppSelector(getSelectedInvoicesLength);
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const selectedTab = useAppSelector(getSelectedTab);
  const rowDataByVendor = useAppSelector(getRowDataByVendor);
  const isChecked = !!selectedInvoicesLength;
  const isIndeterminate = selectedInvoicesLength !== rowData?.length;

  const { handleCustomSort, isSortAscending, isSortDescending } = useCustomSort(
    {
      api,
      colId: AG_GRID_AUTO_COL_ID
    }
  );

  useSelectAllInvoices({ isSelectAll });

  const handleChange = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>): void => {
      if (checked) {
        if (landingPageFlag === true) {
          setIsSelectAll(true);
        } else {
          const invoiceIdsToSelect =
            Object.values(rowDataByVendor).reduce((acc: string[], invoices) => {
              if (acc && invoices) {
                const selectableInvoiceIds = getInvoiceIdsToSelect(
                  invoices,
                  checked,
                  selectedTab
                );
                return [...acc, ...selectableInvoiceIds];
              }
              return acc;
            }, []) || [];
          dispatch(
            selectInvoices({
              invoiceIds: invoiceIdsToSelect,
              selected: checked
            })
          );
        }
      } else {
        dispatch(clearSelectedInvoices());
        setIsSelectAll(false);
      }
    },
    [dispatch, landingPageFlag, rowDataByVendor, selectedTab]
  );

  useEffect(() => {
    const headerCellContainer = document.querySelector(
      HEADER_CHECKBOX_CELL_SELECTOR
    ) as HTMLElement;
    if (headerCellContainer) {
      headerCellContainer.style.width = '100%';
    }
  }, []);

  useEffect(() => {
    setIsSelectAll(false);
  }, [selectedTab, paymentMethods]);

  return (
    <>
      <StyledCheckbox
        name="vendors-table-header-checkbox"
        checked={isChecked}
        indeterminate={isIndeterminate}
        onChange={handleChange}
      />
      <div
        className="ag-header-cell-label"
        role="presentation"
        onClick={handleCustomSort}
      >
        <span className="ag-header-cell-text" role="columnheader">
          <FormattedMessage
            defaultMessage="Vendor No."
            description="Vendor no. column header"
          />
        </span>
        <span className="ag-header-icon ag-sort-ascending-icon">
          {isSortAscending && (
            <span className="ag-icon ag-icon-asc" role="presentation" />
          )}
        </span>
        <span className="ag-header-icon ag-sort-descending-icon">
          {isSortDescending && (
            <span className="ag-icon ag-icon-desc" role="presentation" />
          )}
        </span>
      </div>
    </>
  );
};

export default HeaderCheckboxRenderer;
